import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import * as XLSX from "xlsx";
import { fechahora, helperMultisort, RestarTiempos } from "../../../../../../../services/utils";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  ResultadosGanadores,
  controlesArrayFunc,
  ControlesAtleta,
} from "../../../../../../pages/reports/reportsLogic";
import { calcularSubclasificacion } from "../../../../../../pages/reports/subclasificacionesLogic";
import logoBase64 from "../../../../../../pages/nextEvents/Registry/PrintLabels/logoDashport";

// Mismo rojo/naranja de marca que ya usa TitleResultReport.jsx para las
// filas de título de grupo en la lista de resultados en pantalla (#ef4123).
const COLOR_MARCA = [239, 65, 35];
const NOMBRE_GENERO = { F: "Femenil", V: "Varonil", M: "Mixto" };

const useStyles = makeStyles((theme) => ({
  seccionTitulo: {
    marginTop: 15,
    marginBottom: 5,
  },
  contenido: {
    padding: "8px 24px 24px",
  },
}));

const OPCIONES_CONTENIDO = [
  { value: "todos", label: "Todos los resultados" },
  { value: "finishers", label: "Finishers" },
  { value: "ganadores", label: "Ganadores" },
];

export default function PrintResultsCSV(props) {
  const { business } = props;

  const dispatch = useDispatch();
  const userID = useSelector((state) => state.atlete.userID);
  const atleteAccount = useSelector((state) => state.atlete.atleteAccount);
  const eventSubEvents = useSelector((state) => state.events.eventSubEvents);
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventResults = useSelector((state) => state.events.eventResults);
  const eventConfiguration = useSelector((state) => state.events.eventConfiguration);
  // Ya se carga desde el contenedor que abre este diálogo
  // (AuthorizedUsersResultsContainerNew.jsx dispara getPuntosControl), así
  // que acá solo se lee — es el mismo snapshot que ya usa el detalle de
  // parciales de un atleta individual.
  const puntosControl = useSelector((state) => state.events.puntosControl);

  const [contenido, setContenido] = useState("todos");
  const [formato, setFormato] = useState("excel");
  const [incluirParciales, setIncluirParciales] = useState(false);
  const [incluirAcumulado, setIncluirAcumulado] = useState(true);
  const [incluirDiferencia, setIncluirDiferencia] = useState(true);
  // Qué puntos de control específicos se van a descargar (por kilómetro).
  // Arranca vacío y se llena con "todos seleccionados" en cuanto se conoce
  // la lista disponible (ver useEffect más abajo) — así cambiar de
  // contenido (todos/finishers/ganadores) no deja seleccionados puntos que
  // ya no aplican.
  const [kmSeleccionados, setKmSeleccionados] = useState(new Set());

  // Qué modalidades se van a descargar, y dentro de cada una qué
  // categorías — para evitar bajar resultados que no se van a usar en el
  // momento. Arrancan vacíos y se llenan con "todo seleccionado" en cuanto
  // se conoce el evento (ver useEffect más abajo).
  const [modalidadesSeleccionadas, setModalidadesSeleccionadas] = useState(new Set());
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState({});

  // Subclasificaciones (ej. "Mejores por Ciudad") disponibles para incluir
  // como sección aparte en la descarga. Si el contenido elegido es
  // "ganadores", solo tiene sentido ofrecer las que tienen cantidadGanadores
  // configurado (no hay top-N que recortar si no — ver
  // filasParaSubclasificacion). Para "todos"/"finishers" se ofrecen todas:
  // ahí se descarga el listado completo, no solo el top-N.
  const subclasificacionesDisponibles = useMemo(() => {
    const subclasificaciones =
      (eventConfiguration &&
        eventConfiguration.timingConfig &&
        eventConfiguration.timingConfig.subclasificaciones) ||
      {};
    return Object.keys(subclasificaciones)
      .map((id) => ({ id, ...subclasificaciones[id] }))
      .filter((s) => contenido !== "ganadores" || s.cantidadGanadores);
  }, [eventConfiguration, contenido]);

  const [subclasificacionesSeleccionadas, setSubclasificacionesSeleccionadas] = useState(new Set());

  useEffect(() => {
    setSubclasificacionesSeleccionadas(new Set(subclasificacionesDisponibles.map((s) => s.id)));
  }, [subclasificacionesDisponibles]);

  function toggleSubclasificacion(id) {
    setSubclasificacionesSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const [reporteTodos, setReporteTodos] = useState([]);
  const [reporteFinishers, setReporteFinishers] = useState([]);
  const [reporteGanadores, setReporteGanadores] = useState([]);

  useEffect(() => {
    if (eventResults) {
      setReporteTodos(eventResults);
      setReporteFinishers(eventResults.filter((a) => a.pG !== 0));
      setReporteGanadores(ResultadosGanadores(eventResults));
    }
  }, [eventResults]);

  const controlesArray = useMemo(() => controlesArrayFunc(puntosControl), [
    puntosControl,
  ]);

  // Modalidades del evento con sus categorías reales (por código, estable
  // aunque el nombre cambie) — junta las categorías de ambas ramas de cada
  // modalidad en una sola lista, ya que lo que importa acá es "qué
  // categoría", no "en qué rama está".
  const modalidadesConCategorias = useMemo(() => {
    const resultado = [];
    for (var modKey in eventSubEvents || {}) {
      const codMod = modKey.replace("modalidad_", "");
      const modalidad = eventSubEvents[modKey];
      const categoriasMap = new Map();
      const ramaCat = modalidad.ramaCat || {};
      for (var ramaKey in ramaCat) {
        const categorias = (ramaCat[ramaKey] || {}).categorias || {};
        for (var catKey in categorias) {
          const codCat = catKey.replace("categoria_", "");
          if (!categoriasMap.has(codCat)) {
            categoriasMap.set(codCat, categorias[catKey].nombreCategoria || "Categoría " + codCat);
          }
        }
      }
      resultado.push({
        codMod,
        nombreModalidad: modalidad.descripcionModalidad || "Modalidad " + codMod,
        categorias: Array.from(categoriasMap.entries()).map(([codCat, nombreCategoria]) => ({
          codCat,
          nombreCategoria,
        })),
      });
    }
    return resultado.sort((a, b) => Number(a.codMod) - Number(b.codMod));
  }, [eventSubEvents]);

  // Por defecto se descarga todo — se reinicia cada vez que cambia la
  // configuración del evento (ej. al abrir el diálogo por primera vez).
  useEffect(() => {
    setModalidadesSeleccionadas(new Set(modalidadesConCategorias.map((m) => m.codMod)));
    const todasCategorias = {};
    modalidadesConCategorias.forEach((m) => {
      todasCategorias[m.codMod] = new Set(m.categorias.map((c) => c.codCat));
    });
    setCategoriasSeleccionadas(todasCategorias);
  }, [modalidadesConCategorias]);

  function toggleModalidad(codMod) {
    setModalidadesSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(codMod)) next.delete(codMod);
      else next.add(codMod);
      return next;
    });
  }

  function toggleCategoria(codMod, codCat) {
    setCategoriasSeleccionadas((prev) => {
      const actuales = new Set(prev[codMod] || []);
      if (actuales.has(codCat)) actuales.delete(codCat);
      else actuales.add(codCat);
      return { ...prev, [codMod]: actuales };
    });
  }

  // Lista + nombre de archivo según qué contenido se eligió, ya acotada a
  // las modalidades/categorías marcadas. Los absolutos (ab === 1) no son
  // una "categoría" propiamente — se incluyen con solo marcar su modalidad,
  // sin depender de qué categorías de edad estén marcadas.
  const seleccion = useMemo(() => {
    let datos;
    let nombre;
    if (contenido === "finishers") {
      datos = reporteFinishers;
      nombre = "Resultados Finishers";
    } else if (contenido === "ganadores") {
      datos = reporteGanadores;
      nombre = "Ganadores";
    } else {
      datos = reporteTodos;
      nombre = "Resultados";
    }

    const datosFiltrados = datos.filter((r) => {
      const codMod = String(r.codMod);
      if (!modalidadesSeleccionadas.has(codMod)) return false;
      if (r.ab === 1) return true;
      const categoriasDeEstaModalidad = categoriasSeleccionadas[codMod];
      if (!categoriasDeEstaModalidad) return true;
      return categoriasDeEstaModalidad.has(String(r.codCat));
    });

    return { datos: datosFiltrados, nombre };
  }, [
    contenido,
    reporteTodos,
    reporteFinishers,
    reporteGanadores,
    modalidadesSeleccionadas,
    categoriasSeleccionadas,
  ]);

  // Unión (por kilómetro) de los puntos de control entre todas las
  // modalidades presentes en lo seleccionado — es la lista que se ofrece
  // para elegir cuáles descargar. Cada modalidad igual solo usa los suyos
  // al generar el archivo (ver descargarExcel/descargarPDF).
  const checkpointsDisponibles = useMemo(() => {
    const porKm = new Map();
    (seleccion.datos || []).forEach((r) => {
      const conf = controlesArray["modalidad_" + r.codMod];
      if (conf) {
        conf.titles.forEach((t) => {
          if (!porKm.has(t.kilometro)) porKm.set(t.kilometro, t.descripcionPunto);
        });
      }
    });
    return Array.from(porKm.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([kilometro, descripcionPunto]) => ({ kilometro, descripcionPunto }));
  }, [seleccion, controlesArray]);

  // Por defecto se seleccionan todos los puntos disponibles cada vez que
  // cambia esa lista (ej. al cambiar "contenido"), para no arrastrar una
  // selección de puntos que ya no aplican.
  useEffect(() => {
    setKmSeleccionados(new Set(checkpointsDisponibles.map((c) => c.kilometro)));
  }, [checkpointsDisponibles]);

  function toggleKm(km) {
    setKmSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(km)) next.delete(km);
      else next.add(km);
      return next;
    });
  }

  // Quita el "*" que ControlesAtleta deja en el km 0 cuando hay registro de
  // salida (ver AtleteControls.jsx) — para RestarTiempos hace falta un
  // string "HH:MM:SS" limpio. "N/R" (no registrado) se trata como null: no
  // hay tiempo real con el que calcular ninguna diferencia.
  function tiempoLimpio(valor) {
    if (!valor || valor === "N/R") return null;
    return String(valor).replace("*", "");
  }

  // Por cada punto de control YA CONFIGURADO para la modalidad del atleta
  // (ControlesAtleta, misma función que ya usa la lista de resultados en
  // pantalla), arma {kilometro, descripcionPunto, acumulado, diferencia}.
  // "acumulado" es el tiempo tal cual lo guarda el cronometraje; la
  // "diferencia" es respecto al parcial anterior (arranca en 00:00:00),
  // igual que ya calcula el detalle individual del atleta (AtleteControls.jsx).
  function calcularParcialesAtleta(resultado) {
    const conf = controlesArray["modalidad_" + resultado.codMod];
    if (!conf) return [];
    const controles = ControlesAtleta(puntosControl, resultado, controlesArray);
    const descripcionPorKm = {};
    conf.titles.forEach((t) => {
      descripcionPorKm[t.kilometro] = t.descripcionPunto;
    });

    let anterior = "00:00:00";
    return controles.map((c) => {
      const actual = tiempoLimpio(c.value);
      let diferencia = "N/R";
      if (actual !== null) {
        diferencia = RestarTiempos(anterior, actual);
        anterior = actual;
      }
      return {
        kilometro: c.kilometro,
        descripcionPunto: descripcionPorKm[c.kilometro] || "Km " + c.kilometro,
        acumulado: c.value,
        diferencia,
      };
    });
  }

  // Nombre de hoja de Excel válido: máx. 31 caracteres, sin los símbolos
  // que Excel no permite ( : \ / ? * [ ] ), y sin repetirse dentro del
  // mismo libro.
  function nombreHojaSeguro(nombre, usados) {
    let base = String(nombre || "Modalidad").replace(/[:\\/?*[\]]/g, "").slice(0, 31) || "Modalidad";
    let final = base;
    let n = 2;
    while (usados.has(final)) {
      final = (base.slice(0, 28) + "_" + n).slice(0, 31);
      n++;
    }
    usados.add(final);
    return final;
  }

  // Puntos de control de una modalidad, ya acotados a los que el usuario
  // dejó marcados en "Qué puntos de control incluir" — la diferencia
  // siempre se calcula sobre la secuencia COMPLETA real (calcularParcialesAtleta
  // no filtra), así que ocultar un punto no altera el valor de los demás.
  function columnasParcialesModalidad(conf) {
    if (!conf) return [];
    return [...conf.titles]
      .filter((t) => kmSeleccionados.has(t.kilometro))
      .sort((a, b) => a.kilometro - b.kilometro);
  }

  // Arma, para un atleta, el bloque de columnas de parciales (acumulado y/o
  // diferencia, según lo marcado, por cada punto de control seleccionado,
  // con su nombre real) a insertar antes de Tiempo Oficial/Tiempo Real.
  function columnasParcialesFila(resultado, columnasParciales) {
    const porKm = {};
    calcularParcialesAtleta(resultado).forEach((p) => {
      porKm[p.kilometro] = p;
    });
    const fila = {};
    columnasParciales.forEach((t) => {
      const p = porKm[t.kilometro];
      if (incluirAcumulado) {
        fila[t.descripcionPunto + " (Km " + t.kilometro + ") - Acumulado"] = p ? p.acumulado : "";
      }
      if (incluirDiferencia) {
        fila[t.descripcionPunto + " (Km " + t.kilometro + ") - Diferencia"] = p ? p.diferencia : "";
      }
    });
    return fila;
  }

  // Qué atletas de una subclasificación entran en la descarga, según el
  // contenido principal elegido: en "ganadores" se recorta al top-N
  // configurado (cantidadGanadores); en "todos"/"finishers" se descarga el
  // listado completo (con/sin los que no terminaron, respectivamente) — el
  // top-N solo aplica cuando lo que se está descargando es, justamente, el
  // reporte de ganadores.
  function filasParaSubclasificacion(id, cantidadGanadores) {
    const todas = calcularSubclasificacion(eventResults, id);
    if (contenido === "ganadores") {
      return todas.filter((r) => r.pSub !== null && r.pSub <= cantidadGanadores);
    }
    if (contenido === "finishers") {
      return todas.filter((r) => r.pG > 0);
    }
    return todas;
  }

  // Un Excel con una hoja por modalidad — cada hoja solo tiene los puntos
  // de control reales de SU modalidad (evita la mezcla de columnas vacías
  // que salía al juntar distancias distintas en una sola tabla plana).
  function descargarExcel() {
    const esGanadores = contenido === "ganadores";
    const workbook = XLSX.utils.book_new();
    const nombresUsados = new Set();

    const porModalidad = new Map();
    seleccion.datos.forEach((r) => {
      if (!porModalidad.has(r.codMod)) porModalidad.set(r.codMod, []);
      porModalidad.get(r.codMod).push(r);
    });

    const codigosOrdenados = Array.from(porModalidad.keys()).sort((a, b) => a - b);

    codigosOrdenados.forEach((codMod) => {
      const filasModalidad = porModalidad.get(codMod);
      const conf = controlesArray["modalidad_" + codMod];
      const columnasParciales = incluirParciales ? columnasParcialesModalidad(conf) : [];

      const filasHoja = filasModalidad.map((r) => {
        const fila = esGanadores
          ? {
              Número: r.num,
              "Posición Categoría": r.pCat,
              Nombre: r.nombre,
              Genero: r.gen,
              Categoría: r.cat,
              Modalidad: r.mod,
            }
          : {
              Número: r.num,
              "Posición General": r.pG,
              "Posición Rama": r.pR,
              "Posición Categoría": r.pCat,
              Nombre: r.nombre,
              Genero: r.gen,
              Categoría: r.cat,
              Modalidad: r.mod,
            };

        if (incluirParciales) {
          Object.assign(fila, columnasParcialesFila(r, columnasParciales));
        }

        fila["Tiempo Oficial"] = r.to;
        if (!esGanadores) {
          fila["Tiempo Real"] = r.tc;
          fila["Fecha hora Meta"] = r.fhm;
          fila["Registro Salida"] = r.registroSalida;
          fila["Serie"] = r.serie;
        }
        return fila;
      });

      const infoModalidad = eventSubEvents && eventSubEvents["modalidad_" + codMod];
      const nombreBase = infoModalidad ? infoModalidad.descripcionModalidad : "Modalidad " + codMod;
      const worksheet = XLSX.utils.json_to_sheet(filasHoja);
      XLSX.utils.book_append_sheet(workbook, worksheet, nombreHojaSeguro(nombreBase, nombresUsados));
    });

    // Una hoja POR SUBCLASIFICACIÓN (no por modalidad) — el objetivo es ver
    // a todos los de esa variable juntos (ej. "Mejores por Ciudad" cruza
    // varias modalidades/ramas), no separados como las hojas normales de
    // arriba. Es una sección adicional independiente de qué contenido
    // principal se eligió — ver filasParaSubclasificacion para el recorte
    // top-N (solo aplica cuando el contenido es "ganadores").
    subclasificacionesDisponibles
      .filter((s) => subclasificacionesSeleccionadas.has(s.id))
      .forEach((s) => {
        const filasHoja = filasParaSubclasificacion(s.id, s.cantidadGanadores)
          .map((r) => ({
            Número: r.num,
            Posición: r.pSub,
            Nombre: r.nombre,
            Valor: r.valorSubclasif,
            Genero: r.gen,
            Modalidad: r.mod,
            "Tiempo Oficial": r.to,
          }));
        if (!filasHoja.length) return;
        const worksheet = XLSX.utils.json_to_sheet(filasHoja);
        XLSX.utils.book_append_sheet(workbook, worksheet, nombreHojaSeguro(s.nombre, nombresUsados));
      });

    XLSX.writeFile(
      workbook,
      seleccion.nombre + " - " + eventDetail.nombreEvento + " - " + fechahora() + ".xlsx"
    );
  }

  function descargarPDF() {
    const esGanadores = contenido === "ganadores";

    const doc = new jsPDF();
    const anchoPagina = doc.internal.pageSize.getWidth();
    const altoPagina = doc.internal.pageSize.getHeight();
    const margenLateral = 12;
    const margenEncabezado = 30; // espacio reservado para logo/título en cada página

    // Logo arriba a la izquierda + nombre del evento/reporte arriba a la
    // derecha, en CADA página — didDrawPage lo dispara automáticamente
    // cuando una tabla ocupa más de una página; se guarda qué páginas ya
    // se dibujaron para no repetirlo si dos tablas distintas caen en la
    // misma página (una termina y la siguiente arranca ahí mismo).
    const paginasConEncabezado = new Set();
    function dibujarEncabezado(data) {
      const pageNumber = data.pageNumber;
      if (paginasConEncabezado.has(pageNumber)) return;
      paginasConEncabezado.add(pageNumber);

      const docRef = data.doc;
      const logoAncho = 32;
      const logoAlto = logoAncho / 2.571; // proporción real del PNG (1800x700)
      docRef.addImage(logoBase64, "PNG", margenLateral, 8, logoAncho, logoAlto);

      docRef.setFont(undefined, "bold");
      docRef.setFontSize(12);
      docRef.setTextColor(...COLOR_MARCA);
      docRef.text(eventDetail.nombreEvento, anchoPagina - margenLateral, 15, { align: "right" });

      docRef.setFont(undefined, "normal");
      docRef.setFontSize(9);
      docRef.setTextColor(120, 120, 120);
      docRef.text(seleccion.nombre, anchoPagina - margenLateral, 20, { align: "right" });

      docRef.setDrawColor(...COLOR_MARCA);
      docRef.setLineWidth(0.6);
      docRef.line(margenLateral, margenEncabezado - 4, anchoPagina - margenLateral, margenEncabezado - 4);
    }

    let yCursor = margenEncabezado;

    function tituloGrupo(primerResultado) {
      const infoModalidad = eventSubEvents && eventSubEvents["modalidad_" + primerResultado.codMod];
      const nombreModalidad = infoModalidad
        ? infoModalidad.descripcionModalidad
        : primerResultado.mod + " km";
      const generoTexto = NOMBRE_GENERO[primerResultado.gen] || primerResultado.gen;
      if (primerResultado.valorSubclasif !== undefined) {
        return (
          primerResultado.nombreSubclasificacion +
          ": " +
          primerResultado.valorSubclasif +
          " - " +
          nombreModalidad +
          " - Rama " +
          generoTexto
        );
      }
      return esGanadores
        ? nombreModalidad + " - Rama " + generoTexto + " - Categoría " + primerResultado.cat
        : nombreModalidad + " - Rama " + generoTexto;
    }

    function showTable(tempList) {
      if (tempList.length === 0) return;

      // Sin "Tiempo Oficial" todavía: se agrega al final, después de los
      // parciales (si se incluyen), para que quede justo antes según se
      // pidió. Se calcula por grupo (no una sola vez afuera) porque un
      // mismo PDF puede mezclar tablas normales con tablas de ganadores de
      // subclasificación (columna "Valor" en vez de "Categoría").
      const esSubclasifGrupo = tempList[0].valorSubclasif !== undefined;
      const headerBase = esSubclasifGrupo
        ? ["Número", "Posición", "Nombres", "Modalidad", "Género", "Valor"]
        : esGanadores
        ? ["Número", "Posición", "Nombres", "Modalidad", "Género", "Categoría"]
        : ["Número", "Posición General", "Nombres", "Modalidad", "Género", "Categoría"];

      // Cada tabla agrupa una sola modalidad (ver el corte más abajo), así
      // que acá sí se puede usar el nombre real de SUS puntos de control,
      // sin ambigüedad entre modalidades distintas.
      let columnasParciales = [];
      const conf = controlesArray["modalidad_" + tempList[0].codMod];
      if (incluirParciales) {
        columnasParciales = columnasParcialesModalidad(conf);
      }
      const headerParciales = [];
      columnasParciales.forEach((t) => {
        if (incluirAcumulado) headerParciales.push(t.descripcionPunto + " (Km " + t.kilometro + ") - Acum.");
        if (incluirDiferencia) headerParciales.push(t.descripcionPunto + " (Km " + t.kilometro + ") - Dif.");
      });
      const headerFinal = [...headerBase, ...headerParciales, "Tiempo Oficial"];

      // Si el título de este grupo quedaría pegado al borde inferior de la
      // página, mejor pasar a una nueva página en vez de dejarlo huérfano
      // separado de su tabla.
      if (yCursor > altoPagina - 25) {
        doc.addPage();
        yCursor = margenEncabezado;
      }

      doc.setFont(undefined, "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(...COLOR_MARCA);
      doc.text(tituloGrupo(tempList[0]), margenLateral, yCursor);
      yCursor += 5;

      doc.autoTable({
        head: [headerFinal],
        body: tempList.map((a) => {
          const fila = esSubclasifGrupo
            ? [
                a.num,
                a.pSub !== null && a.pSub !== undefined ? a.pSub : "-",
                a.nombre,
                a.mod + " km",
                a.gen,
                a.valorSubclasif,
              ]
            : [a.num, esGanadores ? a.pCat : a.pG, a.nombre, a.mod + " km", a.gen, a.cat];
          if (incluirParciales) {
            const porKmAtleta = {};
            calcularParcialesAtleta(a).forEach((p) => {
              porKmAtleta[p.kilometro] = p;
            });
            columnasParciales.forEach((t) => {
              const p = porKmAtleta[t.kilometro];
              if (incluirAcumulado) fila.push(p ? p.acumulado : "");
              if (incluirDiferencia) fila.push(p ? p.diferencia : "");
            });
          }
          fila.push(a.to);
          return fila;
        }),
        startY: yCursor,
        margin: { top: margenEncabezado, left: margenLateral, right: margenLateral },
        styles: { fontSize: 8, cellPadding: 2, valign: "middle" },
        headStyles: { fillColor: COLOR_MARCA, textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [250, 241, 237] },
        didDrawPage: dibujarEncabezado,
      });
      yCursor = doc.lastAutoTable.finalY + 10;
    }

    // Se agrupa por modalidad/categoría/género (misma modalidad -> misma
    // cantidad de columnas de parcial), igual que ya hacía el PDF de
    // ganadores — se ordena primero porque "todos"/"finishers" no vienen
    // necesariamente agrupados así.
    const ordenado = helperMultisort(
      [...seleccion.datos],
      ["codMod", "cat", "gen", esGanadores ? "pCat" : "pG"],
      ["ASC", "ASC", "ASC", "ASC"]
    );

    let tempList = [];
    for (var i in ordenado) {
      if (
        ordenado[i - 1] &&
        (ordenado[i - 1].codMod !== ordenado[i].codMod ||
          ordenado[i - 1].cat !== ordenado[i].cat ||
          ordenado[i - 1].gen !== ordenado[i].gen)
      ) {
        showTable(tempList);
        tempList = [];
      }
      tempList.push(ordenado[i]);
    }
    showTable(tempList);

    // Después de las tablas normales, una tabla por cada grupo
    // modalidad+rama+valor de cada subclasificación seleccionada — mismo
    // showTable(), que ya distingue este caso (columna "Valor" en vez de
    // "Categoría", título con el nombre de la subclasificación). Es una
    // sección adicional independiente de qué contenido principal se eligió
    // — ver filasParaSubclasificacion para el recorte top-N (solo aplica
    // cuando el contenido es "ganadores").
    subclasificacionesDisponibles
      .filter((s) => subclasificacionesSeleccionadas.has(s.id))
      .forEach((s) => {
        const ganadoresSub = filasParaSubclasificacion(s.id, s.cantidadGanadores).map((r) => ({
          ...r,
          nombreSubclasificacion: s.nombre,
        }));

        let tempListSub = [];
        for (var j = 0; j < ganadoresSub.length; j++) {
          if (
            j > 0 &&
            (ganadoresSub[j - 1].codMod !== ganadoresSub[j].codMod ||
              ganadoresSub[j - 1].gen !== ganadoresSub[j].gen ||
              ganadoresSub[j - 1].valorSubclasif !== ganadoresSub[j].valorSubclasif)
          ) {
            showTable(tempListSub);
            tempListSub = [];
          }
          tempListSub.push(ganadoresSub[j]);
        }
        showTable(tempListSub);
      });

    // Numeración de página: solo se puede saber el total una vez que ya se
    // dibujó todo, así que se agrega en una pasada final sobre cada página.
    const totalPaginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPaginas; p++) {
      doc.setPage(p);
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        "Página " + p + " de " + totalPaginas,
        anchoPagina - margenLateral,
        altoPagina - 8,
        { align: "right" }
      );
    }

    doc.save(
      seleccion.nombre + " - " + eventDetail.nombreEvento + " - " + fechahora() + ".pdf"
    );
  }

  const classes = useStyles();

  return (
    <Grid container direction="row" alignItems="flex-start">
      <Grid item xs={12} sm={12}>
        {eventResults && (
          <Grid container direction="column" className={classes.contenido}>
            <Typography variant="subtitle1" className={classes.seccionTitulo}>
              ¿Qué quieres descargar?
            </Typography>
            <RadioGroup
              value={contenido}
              onChange={(e) => setContenido(e.target.value)}
            >
              {OPCIONES_CONTENIDO.map((op) => (
                <FormControlLabel
                  key={op.value}
                  value={op.value}
                  control={<Radio color="primary" />}
                  label={op.label}
                />
              ))}
            </RadioGroup>

            {modalidadesConCategorias.length > 0 && (
              <>
                <Typography variant="subtitle1" className={classes.seccionTitulo}>
                  Modalidades y categorías a incluir
                </Typography>
                <div>
                  <Button
                    size="small"
                    onClick={() => {
                      setModalidadesSeleccionadas(
                        new Set(modalidadesConCategorias.map((m) => m.codMod))
                      );
                      const todasCategorias = {};
                      modalidadesConCategorias.forEach((m) => {
                        todasCategorias[m.codMod] = new Set(m.categorias.map((c) => c.codCat));
                      });
                      setCategoriasSeleccionadas(todasCategorias);
                    }}
                  >
                    Seleccionar todo
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setModalidadesSeleccionadas(new Set());
                      setCategoriasSeleccionadas({});
                    }}
                  >
                    Ninguna
                  </Button>
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {modalidadesConCategorias.map((m) => (
                    <div key={m.codMod} style={{ marginBottom: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={modalidadesSeleccionadas.has(m.codMod)}
                            onChange={() => toggleModalidad(m.codMod)}
                          />
                        }
                        label={<strong>{m.nombreModalidad}</strong>}
                      />
                      {modalidadesSeleccionadas.has(m.codMod) && m.categorias.length > 0 && (
                        <FormGroup style={{ paddingLeft: 32 }} row>
                          {m.categorias.map((c) => (
                            <FormControlLabel
                              key={c.codCat}
                              control={
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={
                                    !!(
                                      categoriasSeleccionadas[m.codMod] &&
                                      categoriasSeleccionadas[m.codMod].has(c.codCat)
                                    )
                                  }
                                  onChange={() => toggleCategoria(m.codMod, c.codCat)}
                                />
                              }
                              label={c.nombreCategoria}
                            />
                          ))}
                        </FormGroup>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {subclasificacionesDisponibles.length > 0 && (
              <>
                <Typography variant="subtitle1" className={classes.seccionTitulo}>
                  Subclasificaciones a incluir
                </Typography>
                <FormGroup>
                  {subclasificacionesDisponibles.map((s) => (
                    <FormControlLabel
                      key={s.id}
                      control={
                        <Checkbox
                          color="primary"
                          checked={subclasificacionesSeleccionadas.has(s.id)}
                          onChange={() => toggleSubclasificacion(s.id)}
                        />
                      }
                      label={
                        contenido === "ganadores"
                          ? s.nombre + " (top " + s.cantidadGanadores + ")"
                          : s.nombre + " (listado completo)"
                      }
                    />
                  ))}
                </FormGroup>
              </>
            )}

            <Divider />

            <Typography variant="subtitle1" className={classes.seccionTitulo}>
              Formato
            </Typography>
            <RadioGroup
              row
              value={formato}
              onChange={(e) => setFormato(e.target.value)}
            >
              <FormControlLabel value="excel" control={<Radio color="primary" />} label="Excel" />
              <FormControlLabel value="pdf" control={<Radio color="primary" />} label="PDF" />
            </RadioGroup>
            {formato === "excel" && (
              <Typography variant="caption" color="textSecondary">
                Se genera un archivo con una hoja por cada modalidad.
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={incluirParciales}
                  onChange={(e) => setIncluirParciales(e.target.checked)}
                />
              }
              label="Incluir tiempos parciales de los atletas"
            />

            {incluirParciales && (
              <Grid container direction="column" style={{ paddingLeft: 24, marginBottom: 10 }}>
                <FormLabel component="legend">Qué columnas de tiempo incluir</FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={incluirAcumulado}
                        onChange={(e) => setIncluirAcumulado(e.target.checked)}
                      />
                    }
                    label="Tiempo acumulado"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={incluirDiferencia}
                        onChange={(e) => setIncluirDiferencia(e.target.checked)}
                      />
                    }
                    label="Diferencia con el parcial anterior"
                  />
                </FormGroup>

                {checkpointsDisponibles.length > 0 && (
                  <>
                    <FormLabel component="legend" style={{ marginTop: 10 }}>
                      Qué puntos de control descargar
                    </FormLabel>
                    <div>
                      <Button
                        size="small"
                        onClick={() =>
                          setKmSeleccionados(new Set(checkpointsDisponibles.map((c) => c.kilometro)))
                        }
                      >
                        Seleccionar todos
                      </Button>
                      <Button size="small" onClick={() => setKmSeleccionados(new Set())}>
                        Ninguno
                      </Button>
                    </div>
                    <FormGroup style={{ maxHeight: 180, overflowY: "auto" }}>
                      {checkpointsDisponibles.map((c) => (
                        <FormControlLabel
                          key={c.kilometro}
                          control={
                            <Checkbox
                              color="primary"
                              checked={kmSeleccionados.has(c.kilometro)}
                              onChange={() => toggleKm(c.kilometro)}
                            />
                          }
                          label={c.descripcionPunto + " (Km " + c.kilometro + ")"}
                        />
                      ))}
                    </FormGroup>
                  </>
                )}
              </Grid>
            )}

            <Divider style={{ marginBottom: 15 }} />

            <Button
              variant="contained"
              color="primary"
              style={{ padding: 15, width: "100%" }}
              onClick={() => (formato === "excel" ? descargarExcel() : descargarPDF())}
            >
              Descargar
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
